import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["modal", "loadingIndicator", "errorMessage", "conversionOptions"]
  static values = { 
    applicationId: String,
    saveForLaterUrl: String
  }

  connect() {
    // Bind close modal on ESC key
    this.boundEscapeHandler = this.closeOnEscape.bind(this)
  }

  disconnect() {
    document.removeEventListener('keydown', this.boundEscapeHandler)
  }

  // Main entry point - triggered by Save for Later button
  async saveForLater(event) {
    event.preventDefault()
    
    try {
      // Check if we're on the final decision dashboard step
      const currentStepField = document.querySelector('input[name="application[current_step]"]')
      const isDecisionDashboard = currentStepField && currentStepField.value === 'decision_dashboard'
      
      // Skip form save if we're on the decision dashboard (application is already complete)
      if (!isDecisionDashboard) {
        // First save the current form data
        await this.saveCurrentFormData()
      }
      
      // Then fetch conversion options and show modal
      await this.fetchConversionOptionsAndShowModal()
    } catch (error) {
      console.error('Save for later failed:', error)
      this.showError('Failed to save application. Please try again.')
    }
  }

  async saveCurrentFormData() {
    const form = this.element.querySelector('form') || this.element.closest('form')
    if (!form) {
      throw new Error('Form not found')
    }

    const formData = new FormData(form)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // Always use the correct update URL, not the edit URL
    const updateUrl = `/application/${this.applicationIdValue}`
    
    const response = await fetch(updateUrl, {
      method: 'PATCH',
      body: formData,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'X-CSRF-Token': csrfToken
      }
    })

    if (!response.ok) {
      let errorMessage = 'Save failed'
      try {
        const data = await response.json()
        errorMessage = data.errors ? data.errors.join(', ') : errorMessage
      } catch (e) {
        // Response might be HTML, not JSON
        errorMessage = `Save failed with status: ${response.status}`
      }
      throw new Error(errorMessage)
    }
  }

  async fetchConversionOptionsAndShowModal() {
    this.showLoading()
    
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      
      const response = await fetch(this.saveForLaterUrlValue, {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': csrfToken,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to load save options')
      }

      const data = await response.json()
      this.populateConversionOptions(data.conversion_options)
      this.showModal()
    } catch (error) {
      this.showError(error.message)
    } finally {
      this.hideLoading()
    }
  }

  populateConversionOptions(options) {
    const manualOption = this.conversionOptionsTarget.querySelector('[data-option="manual"]')
    const applicantInfo = this.conversionOptionsTarget.querySelector('[data-applicant-info]')

    if (applicantInfo) {
      applicantInfo.textContent = `Hello, ${options.applicant_name || 'there'}!`
    }

    if (manualOption) {
      const manualButton = manualOption.querySelector('[data-manual-registration]')
      if (manualButton) {
        manualButton.href = `${options.manual_registration_url}?convert_application=true`
      }
    }
  }

  showModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove('hidden')
      this.modalTarget.classList.add('flex')
      document.body.style.overflow = 'hidden'
      document.addEventListener('keydown', this.boundEscapeHandler)
      
      // Focus trap for accessibility
      const firstFocusable = this.modalTarget.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      if (firstFocusable) {
        firstFocusable.focus()
      }
    }
  }

  closeModal(event) {
    if (event) {
      event.preventDefault()
    }
    
    if (this.hasModalTarget) {
      this.modalTarget.classList.add('hidden')
      this.modalTarget.classList.remove('flex')
      document.body.style.overflow = ''
      document.removeEventListener('keydown', this.boundEscapeHandler)
    }
  }

  closeOnEscape(event) {
    if (event.key === 'Escape') {
      this.closeModal()
    }
  }

  // Handle clicking outside modal to close
  backdropClick(event) {
    if (event.target === event.currentTarget) {
      this.closeModal()
    }
  }

  showLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('hidden')
    }
  }

  hideLoading() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('hidden')
    }
  }

  showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove('hidden')
      
      // Auto-hide error after 5 seconds
      setTimeout(() => {
        this.hideError()
      }, 5000)
    }
  }

  hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.classList.add('hidden')
    }
  }

  // Handle manual registration selection
  selectManualRegistration(event) {
    event.preventDefault()
    
    // Show loading state in the button
    const button = event.currentTarget
    const originalContent = button.innerHTML
    button.innerHTML = `
      <div class="flex items-center">
        <svg class="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>Loading registration...</span>
      </div>
    `
    button.disabled = true
    
    // Small delay to show loading state, then navigate
    setTimeout(() => {
      window.location.href = button.href
    }, 300)
  }
}